import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Phone, MapPin, Sparkles, CheckCircle2, XCircle,
  Loader2, Calendar, ExternalLink, GraduationCap, Eye
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

export default function AdminHostApplicationsPage() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [actioning, setActioning] = useState(null);
  const [rejectReason, setRejectReason] = useState({});

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

  const reload = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/host-applications`, { headers: headers() });
      if (r.ok) { const d = await r.json(); setApps(d.applications || []); }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const approve = async (appId) => {
    setActioning(appId);
    try {
      const r = await fetch(`${API}/admin/host-applications/${appId}/approve`, { method: "POST", headers: headers() });
      if (r.ok) {
        const d = await r.json();
        toast.success("Approved · dashboard created");
        navigator.clipboard.writeText(`${window.location.origin}/auth/magic?token=${d.approval_magic_token}`);
        toast.info("Approval magic link copied to clipboard");
        reload();
      } else toast.error("Approve failed");
    } catch { toast.error("Network error"); } finally { setActioning(null); }
  };

  const reject = async (appId) => {
    const reason = rejectReason[appId] || "";
    setActioning(appId);
    try {
      const r = await fetch(`${API}/admin/host-applications/${appId}/reject`, {
        method: "POST", headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({ reason })
      });
      if (r.ok) { toast.success("Rejected"); reload(); }
      else toast.error("Reject failed");
    } catch { toast.error("Network error"); } finally { setActioning(null); }
  };

  const filtered = apps.filter(a => filter === "all" || a.status === filter);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-host-applications-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}><ArrowLeft className="w-4 h-4" /></Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2"><Mail className="w-5 h-5 text-purple-600" /> Host Applications</h1>
              <p className="text-xs text-gray-500">Review pending hosts. Approve to auto-create their dashboard with prefilled brand data.</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {["pending", "approved", "rejected", "all"].map(f => (
              <button key={f} onClick={() => setFilter(f)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === f ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-200"}`} data-testid={`filter-${f}`}>
                {f} {f !== "all" && `(${apps.filter(a => a.status === f).length})`}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-3">
        {loading ? <Loader2 className="w-6 h-6 animate-spin text-purple-500 mx-auto" /> : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-gray-400"><Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />No {filter} applications</CardContent></Card>
        ) : filtered.map(app => (
          <Card key={app.application_id} data-testid={`app-card-${app.application_id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-base">{app.host_name}</h3>
                    <Badge className={`text-[10px] ${STATUS_COLORS[app.status] || ""}`}>{app.status}</Badge>
                    <Badge variant="outline" className="text-[10px] capitalize">{app.category_id}</Badge>
                    {app.has_instructor_services && <Badge variant="outline" className="text-[10px]"><GraduationCap className="w-2.5 h-2.5 mr-0.5" /> Instructor</Badge>}
                    {app.uses_acuity && <Badge variant="outline" className="text-[10px]"><Calendar className="w-2.5 h-2.5 mr-0.5" /> Acuity</Badge>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-600 flex-wrap">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {app.host_email}</span>
                    {app.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {app.phone}</span>}
                    {app.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {app.location}</span>}
                  </div>
                  {app.tagline && <p className="text-sm italic text-gray-700 mt-1">"{app.tagline}"</p>}
                  {app.intro && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{app.intro}</p>}
                  {app.hopes && (
                    <div className="bg-purple-50 rounded p-2 mt-2 border border-purple-100">
                      <p className="text-[10px] uppercase font-semibold text-purple-700 mb-0.5">Hopes</p>
                      <p className="text-xs text-gray-700">{app.hopes}</p>
                    </div>
                  )}
                  {app.enabled_preference_blocks?.length > 0 && (
                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                      <span className="text-[10px] text-gray-500">Collecting:</span>
                      {app.enabled_preference_blocks.map(b => <Badge key={b} variant="secondary" className="text-[10px]">{b}</Badge>)}
                    </div>
                  )}
                  {app.social_links && (
                    <div className="flex items-center gap-2 mt-1.5 text-[11px] text-purple-600">
                      {app.social_links.instagram && <span>IG: {app.social_links.instagram}</span>}
                      {app.social_links.tiktok && <span>TT: {app.social_links.tiktok}</span>}
                      {app.social_links.website && <span>{app.social_links.website}</span>}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <span className="text-[10px] text-gray-400">{app.submitted_at?.slice(0, 10)}</span>
                  {app.event_id && (
                    <Button size="sm" variant="outline" onClick={() => navigate(`/host/v2/${app.event_id}`)} data-testid={`view-dashboard-${app.application_id}`}>
                      <Eye className="w-3 h-3 mr-1" /> View dashboard
                    </Button>
                  )}
                </div>
              </div>

              {app.status === "pending" && (
                <div className="border-t pt-3 flex items-center gap-2 flex-wrap">
                  <Button size="sm" onClick={() => approve(app.application_id)} disabled={actioning === app.application_id} className="bg-green-600 hover:bg-green-700" data-testid={`approve-${app.application_id}`}>
                    {actioning === app.application_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Approve & create dashboard</>}
                  </Button>
                  <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
                    <Input placeholder="Reject reason (optional)" value={rejectReason[app.application_id] || ""} onChange={(e) => setRejectReason({ ...rejectReason, [app.application_id]: e.target.value })} className="text-xs h-8" data-testid={`reject-reason-${app.application_id}`} />
                    <Button size="sm" variant="outline" onClick={() => reject(app.application_id)} disabled={actioning === app.application_id} className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`reject-${app.application_id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              )}

              {app.status === "rejected" && app.rejected_reason && (
                <div className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded p-2"><strong>Reason:</strong> {app.rejected_reason}</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
